import Button, { type ButtonProps } from '@mui/material/Button';
import ButtonBase, { type ButtonBaseProps } from '@mui/material/ButtonBase';
import { useLinkProps, useLocation } from '@tanstack/react-router';
import type { MouseEvent, ReactNode } from 'react';
import type { Signal } from '../../lib/signals.js';
import {
  SIGNALS_SECTION_ID,
  type SignalsSearch,
  signalRunSearch,
} from '../../lib/signals-search.js';

interface LinkOptions {
  search: SignalsSearch;
  onClick?: ((event: MouseEvent<HTMLAnchorElement>) => void) | undefined;
}

interface SignalRunLinkOptions {
  signal: Signal;
  onClick?: LinkOptions['onClick'];
}

// A real anchor to HOME's Signals section, handled in-app (modifier clicks still open a tab). On
// HOME it keeps the dashboard's filters; from another page it brings only the signals keys.
function useSignalsHref({ search, onClick }: LinkOptions) {
  const onDashboard = useLocation({ select: (location) => location.pathname === '/dashboard' });
  const link = useLinkProps({
    to: '/dashboard',
    hash: SIGNALS_SECTION_ID,
    search: (previous: Record<string, unknown>) =>
      onDashboard ? { ...previous, ...search } : search,
  });
  return {
    href: link.href ?? '/dashboard',
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      link.onClick?.(event);
    },
  };
}

export function SignalsButtonLink({
  search,
  onClick,
  ...props
}: Omit<ButtonProps<'a'>, 'href' | 'onClick'> & LinkOptions): ReactNode {
  return <Button component="a" {...props} {...useSignalsHref({ search, onClick })} />;
}

// A real anchor to the Signal's workflow on its run, handled in-app (modifier clicks still open a
// tab). The run's own scope replaces the current page's filters.
function useSignalRunHref(signal: Signal, onClick: LinkOptions['onClick']) {
  const link = useLinkProps({
    to: '/workflows/$workflowId',
    params: { workflowId: signal.actionType },
    search: signalRunSearch(signal),
  });
  return {
    href: link.href ?? '/workflows',
    onClick: (event: MouseEvent<HTMLAnchorElement>) => {
      onClick?.(event);
      link.onClick?.(event);
    },
  };
}

export function SignalRunButtonLink({
  signal,
  onClick,
  ...props
}: Omit<ButtonProps<'a'>, 'href' | 'onClick'> & SignalRunLinkOptions): ReactNode {
  return <Button component="a" {...props} {...useSignalRunHref(signal, onClick)} />;
}

export function SignalRunRowLink({
  signal,
  onClick,
  ...props
}: Omit<ButtonBaseProps<'a'>, 'href' | 'onClick'> & SignalRunLinkOptions): ReactNode {
  return <ButtonBase component="a" {...props} {...useSignalRunHref(signal, onClick)} />;
}
